/**
 * When a Confirmation Number Release lands.
 *
 * Releases are worked by Defera's accounting team, in the office Monday
 * through Friday (Pacific time). A request filed Monday through Friday
 * morning is released within 24 hours. One filed on a Friday afternoon or
 * over the weekend waits for the team: it lands Monday, or Tuesday at the
 * latest.
 *
 * One reading of that rule for every surface that states it: the desk's
 * trip grounding (server/tommy-booking-context.js), the trip page's
 * "Finished paying for your trip?" reply and the release's service card,
 * the release work order (server/confirmation-release.js and its edge
 * twin), and the inquiry's `due_by`, which drives the Staff desk's overdue
 * count. Plain JS, so the browser, Node and Deno share it.
 */

/** The accounting team's clock. */
export const RELEASE_TEAM_TIMEZONE = 'America/Los_Angeles';
/** From this hour on a Friday (team time), a request waits for Monday. */
export const FRIDAY_AFTERNOON_HOUR = 12;
/** The team's working day ends here (team time): the internal deadline. */
export const TEAM_DAY_END_HOUR = 18;
/** The weekday turnaround. */
export const WEEKDAY_RELEASE_HOURS = 24;

const HOUR_MS = 3600000;
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: RELEASE_TEAM_TIMEZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  weekday: 'short',
});

/** The team's wall clock at `date`. */
function teamParts(date) {
  const parts = Object.fromEntries(partsFormatter.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAYS[parts.weekday],
  };
}

/** The instant the team's clock reads `hour`:00 on that calendar day. */
function teamInstant({ year, month, day }, hour) {
  const wall = Date.UTC(year, month - 1, day, hour);
  let guess = wall;
  // The zone's offset at the guess, applied until it settles (twice covers DST).
  for (let i = 0; i < 2; i += 1) {
    const p = teamParts(new Date(guess));
    const offset = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - guess;
    guess = wall - offset;
  }
  return new Date(guess);
}

/** A calendar day `n` days on from `day`. */
function addDays({ year, month, day }, n) {
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

const dayLabel = ({ year, month, day }) => new Date(Date.UTC(year, month - 1, day))
  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });

/**
 * When a release requested at `filedAt` lands.
 *
 * @returns {null | { waitsForMonday: false, latestBy: Date }
 *   | { waitsForMonday: true, mondayLabel: string, tuesdayLabel: string, latestBy: Date }}
 *   `latestBy` is the internal deadline: 24 hours on a weekday; the end of
 *   the team's Tuesday otherwise.
 */
export function releaseWindow(filedAt) {
  const filed = filedAt instanceof Date ? filedAt : new Date(filedAt);
  if (Number.isNaN(filed.getTime())) return null;
  const p = teamParts(filed);
  const waitsForMonday = p.weekday === 6 || p.weekday === 0 || (p.weekday === 5 && p.hour >= FRIDAY_AFTERNOON_HOUR);
  if (!waitsForMonday) {
    return { waitsForMonday: false, latestBy: new Date(filed.getTime() + WEEKDAY_RELEASE_HOURS * HOUR_MS) };
  }
  const monday = addDays(p, { 5: 3, 6: 2, 0: 1 }[p.weekday]);
  const tuesday = addDays(monday, 1);
  return {
    waitsForMonday: true,
    mondayLabel: dayLabel(monday),
    tuesdayLabel: dayLabel(tuesday),
    latestBy: teamInstant(tuesday, TEAM_DAY_END_HOUR),
  };
}

/** What the customer is told, for a release requested at `filedAt`. */
export function releaseTimingSentence(filedAt) {
  const w = releaseWindow(filedAt);
  if (!w?.waitsForMonday) return 'Please allow up to 24 hours.';
  return `Confirmation numbers are released by our accounting team Monday through Friday, so yours will arrive on ${w.mondayLabel}, or ${w.tuesdayLabel} at the latest.`;
}
